import { AppProps } from "next/app";
import Router from "next/router";
import Script from "next/script";
import getConfig from "next/config";
import { useEffect } from "react";
import type { NextPage } from "next";

import { ClientSideAbTestProvider } from "utils/abTesting";
import { withAuthContext } from "utils/auth";
import { loadDataLayer, getDataLayer } from "utils/analytics";
import { pauseAdsEffects } from "utils/ads";
import { VwoHideSnippet } from "utils/vwo";
import deferUntilLoad from "utils/deferUntilLoad";
import SiteMetadata from "components/SiteMetadata";

// Global, application-wide css imports
import "fonts.scss";
import "normalize.css";
import "app.scss";

const { publicRuntimeConfig } = getConfig();

type WebVitalsMetric = {
  id: string;
  label: "web-vital" | "custom";
  name: string;
  startTime: number;
  value: number;
};

type NextPageWithDisableAnalytics = NextPage & {
  disableAnalytics?: boolean;
};

type AppPropsWithDisableAnalytics = AppProps & {
  Component: NextPageWithDisableAnalytics;
};

export const reportWebVitals = (metric: WebVitalsMetric) => {
  const dataLayer = getDataLayer<{
    event: "performanceMark";
    performance: WebVitalsMetric;
  }>();

  dataLayer.push({
    event: "performanceMark",
    performance: metric,
  });
};

const App = ({ Component, pageProps }: AppPropsWithDisableAnalytics) => {
  useEffect(() => {
    // Always stop the ads from running operations during routing
    Router.events.on("routeChangeStart", pauseAdsEffects);
    return () => {
      Router.events.off("routeChangeStart", pauseAdsEffects);
    };
  }, []);

  useEffect(() => {
    // page component can have an optional disableAnalytics property to bypass loading GTM
    if (Component.disableAnalytics) return;

    deferUntilLoad(() => {
      loadDataLayer();
    });
  }, [Component.disableAnalytics]);

  return (
    <ClientSideAbTestProvider>
      <SiteMetadata />
      <VwoHideSnippet />
      <Component {...pageProps} />
      <Script
        src={publicRuntimeConfig.ADS_LIB_URL}
        strategy="afterInteractive"
      />
    </ClientSideAbTestProvider>
  );
};

export default withAuthContext(App);
